import React from 'react'
import styled, { css } from 'styled-components'

import { JobItemDiscountType, Maybe } from '../../common/types'
import { calculateNetSum, calculateVat, hasUndefinedPrice } from './priceCalculations'
import { calculateOrderTotals } from './calculateOrderTotals'
import { JobItemInput } from './types'

interface Props {
  jobItems: JobItemInput[]
  discountItem: Maybe<JobItemInput>
  itemsIncludeVat: boolean
}

const formatAmount = (value: number) => value.toFixed(2).replace('.', ',')

const getLineTotal = (item: JobItemInput, itemsIncludeVat: boolean) =>
  calculateNetSum(item, itemsIncludeVat) + calculateVat(item, itemsIncludeVat)

const getDiscountLineTotal = (
  discountItem: JobItemInput,
  jobItems: JobItemInput[],
  itemsIncludeVat: boolean
) => {
  const totals = calculateOrderTotals({ jobItems, itemsIncludeVat, discountItem })
  if (!totals) return 0
  return totals.discountGrossTotal
}

export const JobItemsReadOnlyList: React.FC<Props> = ({ jobItems, discountItem, itemsIncludeVat }) => {
  const totals = calculateOrderTotals({ jobItems, itemsIncludeVat, discountItem })

  return (
    <Table>
      <HeaderRow>
        <HeaderCell align="left">Tuotenimike</HeaderCell>
        <HeaderCell align="right">Määrä</HeaderCell>
        <HeaderCell align="right">Yksikkö</HeaderCell>
        <HeaderCell align="right">Yks. hinta</HeaderCell>
        <HeaderCell align="right">Ale-%</HeaderCell>
        <HeaderCell align="right">Yhteensä</HeaderCell>
      </HeaderRow>

      {jobItems.map((item, index) => (
        <ItemRow key={item.id ?? index}>
          <Cell align="left" $isName>
            {item.name}
          </Cell>
          <Cell align="right">
            <MobileLabel>Määrä</MobileLabel>
            {formatAmount(item.quantity)}
          </Cell>
          <Cell align="right">
            <MobileLabel>Yksikkö</MobileLabel>
            {item.unit}
          </Cell>
          <Cell align="right">
            <MobileLabel>Yks. hinta</MobileLabel>
            {hasUndefinedPrice(item) ? 'N/A' : formatAmount(item.unitPrice)}
          </Cell>
          <Cell align="right">
            <MobileLabel>Ale-%</MobileLabel>
            {formatAmount(item.discount)}
          </Cell>
          <Cell align="right">
            <MobileLabel>Yhteensä</MobileLabel>
            {hasUndefinedPrice(item) ? 'N/A' : formatAmount(getLineTotal(item, itemsIncludeVat))}
          </Cell>
        </ItemRow>
      ))}

      {discountItem && (
        <ItemRow>
          <Cell align="left" $isName>
            {discountItem.name}
          </Cell>
          <Cell align="right" $hideOnMobile />
          <Cell align="right" $hideOnMobile />
          <Cell align="right">
            <MobileLabel>Ale</MobileLabel>
            {discountItem.discountType === JobItemDiscountType.percentage
              ? `${formatAmount(Math.abs(discountItem.unitPrice))} %`
              : formatAmount(Math.abs(discountItem.unitPrice))}
          </Cell>
          <Cell align="right" $hideOnMobile />
          <Cell align="right">
            <MobileLabel>Yhteensä</MobileLabel>
            {formatAmount(getDiscountLineTotal(discountItem, jobItems, itemsIncludeVat))}
          </Cell>
        </ItemRow>
      )}

      <FooterSection>
        <FooterSeparator />

        {totals && totals.discountNetTotal !== 0 && (
          <FooterRow>
            <FooterLabel>Alennus (netto/brutto)</FooterLabel>
            <FooterValue>
              {totals.hasUndefinedPrice
                ? 'N/A'
                : `${formatAmount(totals.discountNetTotal)} / ${formatAmount(totals.discountGrossTotal)}`}
            </FooterValue>
          </FooterRow>
        )}

        <FooterRow>
          <FooterLabel>Veroton summa</FooterLabel>
          <FooterValue>
            {totals?.hasUndefinedPrice ? 'N/A' : formatAmount(totals?.netAmountWithDiscountItem ?? 0)}
          </FooterValue>
        </FooterRow>

        <FooterRow>
          <FooterLabel>Veron määrä</FooterLabel>
          <FooterValue>
            {totals?.hasUndefinedPrice ? 'N/A' : formatAmount(totals?.vatAmountWithDiscountItem ?? 0)}
          </FooterValue>
        </FooterRow>

        <FooterRow>
          <FooterLabel bold>Yhteensä</FooterLabel>
          <FooterValue bold>
            {totals?.hasUndefinedPrice ? 'N/A' : formatAmount(totals?.grossAmountWithDiscountItem ?? 0)}
          </FooterValue>
        </FooterRow>
      </FooterSection>
    </Table>
  )
}

const Table = styled.div`
  display: grid;
  grid-template-columns: 3fr 1fr 1fr 1fr 1fr 1fr;
  column-gap: 1rem;
  row-gap: 0.5rem;
  width: 100%;

  @media (max-width: ${props => props.theme.screenSize.tablet}) {
    display: flex;
    flex-direction: column;
  }
`

const HeaderRow = styled.div`
  display: contents;

  @media (max-width: ${props => props.theme.screenSize.tablet}) {
    display: none;
  }
`

const ItemRow = styled.div`
  display: contents;

  @media (max-width: ${props => props.theme.screenSize.tablet}) {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    padding: 0.75rem 0;
    border-bottom: 1px solid ${props => props.theme.colors.grey300};
  }
`

const MobileLabel = styled.span`
  display: none;

  @media (max-width: ${props => props.theme.screenSize.tablet}) {
    display: inline;
    color: ${props => props.theme.colors.grey};
    flex-shrink: 0;
  }
`

interface AlignProps {
  align?: 'left' | 'right'
  bold?: boolean
  $isName?: boolean
  $hideOnMobile?: boolean
}

const alignStyle = css<AlignProps>`
  text-align: ${props => props.align ?? 'left'};
  ${props =>
    props.bold &&
    css`
      font-weight: ${props.theme.fontWeight.bold};
    `};
`

const HeaderCell = styled.div<AlignProps>`
  ${alignStyle}
  font-weight: ${props => props.theme.fontWeight.bold};
  color: ${props => props.theme.colors.black};
`

const Cell = styled.div<AlignProps>`
  ${alignStyle}
  color: ${props => props.theme.colors.black};

  @media (max-width: ${props => props.theme.screenSize.tablet}) {
    display: ${props => (props.$hideOnMobile ? 'none' : 'flex')};
    justify-content: space-between;
    align-items: baseline;
    text-align: left;

    ${props =>
      props.$isName &&
      css`
        font-weight: ${props.theme.fontWeight.bold};
        margin-bottom: 0.25rem;
      `}
  }
`

const FooterSection = styled.div`
  display: contents;

  @media (max-width: ${props => props.theme.screenSize.tablet}) {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    margin-top: 0.5rem;
    padding-top: 0.75rem;
    border-top: 1px solid ${props => props.theme.colors.grey300};
  }
`

const FooterRow = styled.div`
  display: contents;

  @media (max-width: ${props => props.theme.screenSize.tablet}) {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 1rem;
  }
`

const FooterSeparator = styled.div`
  grid-column: 4 / 7;
  border-top: 1px solid ${props => props.theme.colors.grey300};
  margin-top: 0.5rem;
  padding-top: 0.5rem;

  @media (max-width: ${props => props.theme.screenSize.tablet}) {
    display: none;
  }
`

const FooterLabel = styled.div<AlignProps>`
  grid-column: 4 / 6;
  ${alignStyle}
  text-align: right;
  color: ${props => props.theme.colors.black};

  @media (max-width: ${props => props.theme.screenSize.tablet}) {
    grid-column: unset;
    text-align: left;
  }
`

const FooterValue = styled.div<AlignProps>`
  grid-column: 6;
  ${alignStyle}
  text-align: right;
  color: ${props => props.theme.colors.black};

  @media (max-width: ${props => props.theme.screenSize.tablet}) {
    grid-column: unset;
    flex-shrink: 0;
  }
`
